import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Activity, Lock } from 'lucide-react';

const AboutSection = () => {
  const highlights = [
    {
      icon: <Shield className="w-6 h-6 text-[#2dd4bf]" />,
      title: "Adversarial Defense",
      desc: "Protecting healthcare AI models from sophisticated injection and evasion attacks."
    },
    {
      icon: <Activity className="w-6 h-6 text-[#2dd4bf]" />,
      title: "Clinical Integrity",
      desc: "Ensuring that AI-driven diagnostics remain untampered and reliable for patient care."
    },
    {
      icon: <Lock className="w-6 h-6 text-[#2dd4bf]" />,
      title: "Data Sovereignty",
      desc: "Securing the $75B+ global market by preventing model theft and data leaks."
    }
  ];

  return (
    <section id="about" className="relative bg-[#020617] py-24 lg:py-32 overflow-hidden">
      {/* Background Gradient Blur */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[500px] h-[500px] bg-[#2dd4bf]/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="inline-block px-4 py-1 rounded-full border border-[#2dd4bf]/20 bg-[#2dd4bf]/5 text-[#2dd4bf] text-xs font-medium tracking-widest uppercase mb-6">
              Our Mission
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8 leading-tight">
              Bridging the gap between <span className="bg-gradient-to-r from-[#2dd4bf] to-blue-400 bg-clip-text text-transparent">AI Innovation</span> and Healthcare Safety.
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-10 max-w-xl">
              CipherGenix was founded to secure the clinical frontier. As AI revolutionizes diagnostics and patient management, we provide the invisible shield that ensures these models remain secure, private, and compliant.
            </p>

            <div className="space-y-6">
              {highlights.map((item, idx) => (
                <div key={idx} className="flex gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center group-hover:border-[#2dd4bf]/50 transition-colors">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Right: Visual/Image Placeholder */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative z-10 rounded-3xl border border-white/10 bg-slate-900/50 backdrop-blur-xl p-8 overflow-hidden">
               {/* Abstract decorative graphic */}
              <div className="aspect-square rounded-2xl bg-gradient-to-br from-[#0f172a] to-[#020617] flex items-center justify-center relative">
                <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20" />
                
                {/* Visual Representation of "Securing the Revolution" */}
                <div className="relative">
                  <div className="w-48 h-48 rounded-full border-2 border-[#2dd4bf]/20 animate-pulse" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img 
                      src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/user_691713e6ad5c4c8f0a9a730b/f8dfa1ffd_SAVE_20250228_130825.jpg"
                      alt="CipherGenix Core"
                      className="w-24 h-24 rounded-full object-cover border-4 border-[#020617] shadow-2xl shadow-[#2dd4bf]/20"
                    />
                  </div>
                </div>
              </div>

              {/* Stats Overlay */}
              <div className="absolute bottom-12 right-12 p-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 shadow-xl">
                <p className="text-[#2dd4bf] font-bold text-2xl">99.8%</p>
                <p className="text-white text-[10px] uppercase tracking-widest">Detection Rate</p>
              </div>
            </div>
            
            {/* Glow effect behind the image */}
            <div className="absolute -inset-4 bg-[#2dd4bf]/20 blur-3xl rounded-full opacity-50 pointer-events-none" />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default AboutSection;